/**
 * THE TWO PLATFORM ANSWERS THIS PRODUCT ASKS FOR, BUILT ONCE EACH.
 *
 * `Intl.DisplayNames` says what a region or a language is CALLED and
 * `Intl.Collator` says what ORDER two names go in. Both are decisions, and both
 * are expensive to construct — so a caller that builds its own is making a
 * private decision at a cost it pays again on every render that asks.
 *
 * The cost was real: the Person title sheet built a fresh region table for
 * every country cell it drew. The disagreement was worse — three collators were
 * built with three option sets, so the same two names could sort one way in the
 * Person directory and another in the recipient picker, and a Hebrew or Russian
 * list read differently on the two screens.
 *
 * Both answers are cached under everything that changes them, so a second
 * caller asking the same question gets the SAME object rather than a second
 * opinion. A source rule holds the construction to this file.
 */

const displayNames = new Map<string, Intl.DisplayNames>()

/** What `type` of thing the platform is being asked to name. */
export type DisplayNameType = 'region' | 'language'

function namesFor(locale: string, type: DisplayNameType): Intl.DisplayNames {
  const key = `${type}:${locale}`
  let names = displayNames.get(key)
  if (!names) {
    names = new Intl.DisplayNames([locale], { type })
    displayNames.set(key, names)
  }
  return names
}

/**
 * A code's name in `locale`, or THE CODE ITSELF when the runtime cannot name
 * it. Returning the code rather than a blank keeps a selected value visible and
 * identifiable instead of rendering something the reader cannot describe — and
 * an unknown tag is the ordinary case here, because the server accepts any BCP
 * 47 tag it was given while the browser's tables are finite.
 */
export function platformName(code: string, locale: string, type: DisplayNameType): string {
  try {
    return namesFor(locale, type).of(code) ?? code
  } catch {
    return code
  }
}

const collators = new Map<string, Intl.Collator>()

/**
 * The reading order of two names in `locale`.
 *
 * `sensitivity: 'base'` is not a caller's choice: a list of names ordered by
 * case or by accent is ordered by how the value was typed rather than by what
 * it says. NUMERIC ordering is the one option a caller has, because a list
 * whose names carry digits — room 2 before room 10 — is the only place the two
 * answers differ, and a catalogue of country names has no digits to differ on.
 */
export function collatorFor(locale: string, options: { numeric?: boolean } = {}): Intl.Collator {
  const numeric = options.numeric ?? false
  const key = `${numeric ? 'n' : 'a'}:${locale}`
  let collator = collators.get(key)
  if (!collator) {
    collator = new Intl.Collator(locale, { numeric, sensitivity: 'base' })
    collators.set(key, collator)
  }
  return collator
}
